import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { Search } from 'lucide-react';
import { cn } from './utils';

export type PaletteEntry = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Context label shown at the right of the row (customer: pillar; ops: section). */
  pillar: string;
  group?: string;
};

// 'zinc' keeps the customer portal's original hard-coded look; 'semantic'
// uses theme tokens so the ops portal (violet theme, dark mode) renders right.
const TONES = {
  zinc: {
    panel: 'border-zinc-200 bg-white',
    divider: 'border-zinc-200',
    muted: 'text-zinc-400',
    text: 'text-zinc-900',
    placeholder: 'placeholder:text-zinc-400',
    hover: 'hover:bg-zinc-50',
    first: 'bg-zinc-50',
  },
  semantic: {
    panel: 'border-border bg-popover',
    divider: 'border-border',
    muted: 'text-muted-foreground',
    text: 'text-foreground',
    placeholder: 'placeholder:text-muted-foreground',
    hover: 'hover:bg-accent',
    first: 'bg-accent',
  },
} as const;

/**
 * SIL-364: ⌘K / Ctrl+K command palette — searches nav items only (label +
 * pillar/group context), never entity data (assets/lots/SKUs). That's a
 * deliberate scope line from the spec: this is page navigation, not a
 * global search.
 *
 * SIL-1865: shared by the customer and ops portals. Router-agnostic - the
 * host passes `onNavigate`.
 */
export function NavCommandPalette({
  open,
  onClose,
  entries,
  onNavigate,
  tone = 'zinc',
}: {
  open: boolean;
  onClose: () => void;
  entries: PaletteEntry[];
  onNavigate: (to: string) => void;
  tone?: keyof typeof TONES;
}) {
  const t = TONES[tone];
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      // Focus on open — the trigger (rail button or ⌘K) doesn't carry focus in.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => `${e.label} ${e.pillar} ${e.group ?? ''}`.toLowerCase().includes(q));
  }, [entries, query]);

  if (!open) return null;

  function go(to: string) {
    onClose();
    onNavigate(to);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search pages"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-24"
      onClick={onClose}
    >
      <div
        className={cn('w-full max-w-md overflow-hidden rounded-lg border shadow-xl', t.panel)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn('flex items-center gap-2 border-b px-3 py-2.5', t.divider)}>
          <Search className={cn('size-4 shrink-0', t.muted)} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) go(results[0].to);
            }}
            placeholder="Search pages…"
            aria-label="Search pages"
            className={cn('w-full border-0 bg-transparent text-sm outline-none', t.text, t.placeholder)}
          />
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 && (
            <p className={cn('px-3 py-6 text-center text-sm', t.muted)}>No pages match &ldquo;{query}&rdquo;</p>
          )}
          {results.map((entry, i) => (
            <button
              key={entry.to}
              type="button"
              onClick={() => go(entry.to)}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm',
                t.hover,
                i === 0 && t.first,
              )}
            >
              <entry.icon className={cn('size-4 shrink-0', t.muted)} />
              <span className={t.text}>{entry.label}</span>
              <span className={cn('ml-auto text-xs', t.muted)}>
                {entry.group ? `${entry.pillar} / ${entry.group}` : entry.pillar}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
